import { ArrowRight, Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";
import type { OptimizedProfile } from "~/hooks/useOptimizedProfiles";
import type { RecipeChangeRequest } from "~/types/recipeChange";
import { formatDate } from "~/utils/helpers";

import { RecipeChangeStatusBadge } from "./RecipeChangeStatusBadge";

interface RecipeChangeDetailSheetProps {
  request: RecipeChangeRequest | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApproveClick: () => void;
  onRejectClick: () => void;
  isApprover: boolean;
  isApproving: boolean;
  currentUserId?: string | null;
  profiles: Record<string, OptimizedProfile>;
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex justify-between gap-4 border-b border-gray-200 py-1.5 text-sm last:border-0">
      <span className="text-gray-500">{label}</span>
      <span className="max-w-[60%] text-right font-medium text-gray-800">
        {value || "-"}
      </span>
    </div>
  );
}

export function RecipeChangeDetailSheet({
  request,
  open,
  onOpenChange,
  onApproveClick,
  onRejectClick,
  isApprover,
  isApproving,
  currentUserId,
  profiles,
}: RecipeChangeDetailSheetProps) {
  if (!request) return null;

  const isSelfRequest =
    !!request.requested_by && request.requested_by === currentUserId;

  const requesterName =
    (request.requested_by && profiles[request.requested_by]?.full_name) ||
    "Desconocido";
  const reviewerName =
    request.reviewed_by && profiles[request.reviewed_by]?.full_name;
  const changes = request.changes ?? [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <SheetTitle>{request.recipe_name || "Receta"}</SheetTitle>
            <RecipeChangeStatusBadge status={request.status} />
          </div>
          <SheetDescription>
            Cambio de receta propuesto para autorización.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div className="rounded-lg border border-gray-200 bg-gray-100/80 p-4">
            <InfoRow label="Solicitado por" value={requesterName} />
            <InfoRow
              label="Fecha"
              value={formatDate(request.requested_at)}
            />
            <InfoRow label="Motivo" value={request.reason} />
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-gray-800">
              Cambios propuestos ({changes.length})
            </h3>
            {changes.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Sin diferencias detectadas.
              </p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-gray-300">
                <table className="w-full text-sm">
                  <thead className="bg-gray-100 text-gray-600">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">
                        Ingrediente
                      </th>
                      <th className="px-3 py-2 text-left font-medium">Campo</th>
                      <th className="px-3 py-2 text-right font-medium">
                        Cambio
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {changes.map((c, i) => (
                      <tr key={i} className="border-t border-gray-200">
                        <td className="px-3 py-2 text-gray-800">
                          {c.name}
                          {c.code ? (
                            <span className="block text-xs text-muted-foreground">
                              {c.code}
                            </span>
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{c.field}</td>
                        <td className="px-3 py-2 text-right">
                          <span className="inline-flex items-center gap-1.5">
                            <span className="text-red-700 line-through">
                              {c.old ?? "—"}
                            </span>
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            <span className="font-semibold text-green-700">
                              {c.new ?? "—"}
                            </span>
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {request.status !== "pending" && (
            <div className="rounded-lg border border-gray-200 bg-gray-100/80 p-4">
              <InfoRow
                label={
                  request.status === "approved" ? "Autorizado por" : "Rechazado por"
                }
                value={reviewerName}
              />
              <InfoRow
                label="Fecha resolución"
                value={formatDate(request.reviewed_at)}
              />
              {request.rejection_reason && (
                <InfoRow
                  label="Motivo del rechazo"
                  value={request.rejection_reason}
                />
              )}
            </div>
          )}

          {request.status === "pending" && isApprover && (
            <div className="space-y-2">
              {isSelfRequest && (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
                  No puede autorizar su propia solicitud; debe hacerlo otra
                  persona del área.
                </p>
              )}
              <div className="flex gap-3">
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  onClick={onApproveClick}
                  disabled={isApproving || isSelfRequest}
                >
                  {isApproving && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Autorizar
                </Button>
                <Button
                  variant="outline"
                  className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                  onClick={onRejectClick}
                  disabled={isApproving}
                >
                  Rechazar
                </Button>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
